import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { MoreVertical, Eye, Pencil, Trash2, Download, FileText, Copy } from 'lucide-react';

/**
 * Reusable 3-dot row-actions menu. Pass any combination of action handlers; only
 * the actions you provide will render as menu items.
 *
 * Props:
 *   onView, onEdit, onDelete, onDownloadPdf, onSavePdf, onPrint
 *   extraItems = [{ label, icon, onClick, danger?, testId? }]
 *   testIdPrefix = string used to build data-testids
 *   confirmDelete = bool (defaults to true) — show window.confirm before onDelete
 *   deleteLabel = string for the confirm prompt
 *   align = 'end'|'start' — dropdown alignment
 */
export default function RowActions({
  onView, onEdit, onDelete, onDownloadPdf, onSavePdf, onPrint, onDuplicate,
  extraItems = [],
  testIdPrefix = 'row',
  confirmDelete = true,
  deleteLabel = 'this item',
  align = 'end',
  buttonClassName = '',
}) {
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    if (confirmDelete && !window.confirm(`Delete ${deleteLabel}? This cannot be undone.`)) return;
    setBusy(true);
    try { await onDelete?.(); } finally { setBusy(false); }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid={`${testIdPrefix}-actions-trigger`}
          className={`h-8 w-8 p-0 ${buttonClassName}`}
          disabled={busy}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} onClick={(e) => e.stopPropagation()} className="w-44">
        {onView && (
          <DropdownMenuItem data-testid={`${testIdPrefix}-action-view`} onClick={onView}>
            <Eye className="w-4 h-4 mr-2 text-sky-600" />View
          </DropdownMenuItem>
        )}
        {onEdit && (
          <DropdownMenuItem data-testid={`${testIdPrefix}-action-edit`} onClick={onEdit}>
            <Pencil className="w-4 h-4 mr-2 text-amber-600" />Edit
          </DropdownMenuItem>
        )}
        {onDuplicate && (
          <DropdownMenuItem data-testid={`${testIdPrefix}-action-duplicate`} onClick={onDuplicate}>
            <Copy className="w-4 h-4 mr-2 text-indigo-600" />Duplicate
          </DropdownMenuItem>
        )}
        {(onDownloadPdf || onSavePdf) && (
          <DropdownMenuItem data-testid={`${testIdPrefix}-action-pdf`} onClick={onDownloadPdf || onSavePdf}>
            <Download className="w-4 h-4 mr-2 text-emerald-600" />Download PDF
          </DropdownMenuItem>
        )}
        {onPrint && (
          <DropdownMenuItem data-testid={`${testIdPrefix}-action-print`} onClick={onPrint}>
            <FileText className="w-4 h-4 mr-2 text-slate-600" />Print / Save
          </DropdownMenuItem>
        )}
        {extraItems.map((it, idx) => {
          const Icon = it.icon || FileText;
          return (
            <DropdownMenuItem key={idx} data-testid={it.testId || `${testIdPrefix}-action-extra-${idx}`} onClick={it.onClick}>
              <Icon className={`w-4 h-4 mr-2 ${it.danger ? 'text-rose-600' : 'text-slate-600'}`} />{it.label}
            </DropdownMenuItem>
          );
        })}
        {onDelete && <>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid={`${testIdPrefix}-action-delete`} onClick={handleDelete} className="text-rose-600 focus:text-rose-700 focus:bg-rose-50">
            <Trash2 className="w-4 h-4 mr-2" />Delete
          </DropdownMenuItem>
        </>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
